import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import type { ContentChildren } from '@marxbiotech/shared-types/cms';
import type { LayoutCustomerData } from '@marxbiotech/shared-types/bff';
import {
  getPaymentDepositEnabledBySetting,
  getSettlementTypeBySetting,
} from '@lib/util/customer-settlement';
import { usePaymentConfirm } from '@lib/hooks/use-payment-confirm';
import { usePriceChangedConfirm } from '@lib/hooks/use-price-changed-confirm';
import { useConfirmStorage } from '@lib/hooks/use-confirm-storage';

/**
 * Parse a date string to epoch millis, returning `undefined` if the result is NaN.
 *
 * Design Decision: No console.warn on parse failure. The BFF controls the date
 * format (ISO 8601 from Medusa), so malformed dates indicate a BFF bug that
 * should be caught by BFF-side validation/tests, not client-side logging.
 */
function toEpochMillis(value: string): number | undefined {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
}

// =============================================================================
// Types
// =============================================================================

/**
 * Tracks which confirmation modals are currently visible
 *
 * Design Decision: This type is intentionally separate from ConfirmStorage
 * (use-confirm-storage.ts) even though they have the same structure:
 * - ConfirmStorage: Tracks "skipped for 24 hours" state in localStorage
 * - ConfirmModalVisibility: Tracks which modals are currently visible in UI
 *
 * @see PR Review 2026-01-24: Type Design Issue #2
 */
export interface ConfirmModalVisibility {
  payment_confirm: boolean;
  price_changed_confirm: boolean;
}

export interface PriceChangedContent {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** CMS rich text content from Payload Lexical editor */
  content: ContentChildren[];
}

export interface ConfirmModalContextValue {
  /** Open the confirm modal */
  openModal: () => void;
  /** Close the confirm modal */
  closeModal: (confirmed?: boolean) => void;
  /** Whether payment has been confirmed this month */
  isPaymentConfirmed: boolean;
  /** Whether price change has been confirmed */
  isPriceChangedConfirmed: boolean;
  /**
   * Whether any modal is currently open (convenience property)
   *
   * Design Decision: Kept as convenience property alongside openModals.
   * While technically derivable from openModals, it provides clearer semantics
   * for consumers who only need to know "is anything open?" without caring
   * about which specific modal.
   *
   * @see PR Review 2026-01-24: Type Design Issue #4
   */
  isModalOpen: boolean;
  /** Which modals are currently visible */
  openModals: ConfirmModalVisibility;
  /** Skip payment confirmation for 24 hours */
  skipPaymentConfirm: () => void;
  /** Confirm payment and update customer metadata */
  confirmPayment: () => Promise<void>;
  /** Confirm price changed and update customer metadata */
  confirmPriceChanged: () => Promise<void>;
  /** Whether customer is monthly settlement type */
  isMonthlySettlement: boolean;
  /** Price changed content from CMS */
  priceChangedContent: PriceChangedContent | null;
}

// =============================================================================
// Context
// =============================================================================

const ConfirmModalContext = createContext<ConfirmModalContextValue | null>(
  null
);

// =============================================================================
// Customer type for confirm modal (subset of LayoutCustomerData)
// =============================================================================

export type ConfirmModalCustomer = Pick<
  LayoutCustomerData,
  'paymentConfirm' | 'priceChangedConfirm' | 'createdAt' | 'salesChannelSetting'
>;

// =============================================================================
// Provider Props
// =============================================================================

interface ConfirmModalProviderProps {
  children: ReactNode;
  customer: ConfirmModalCustomer | null;
  isActive: boolean;
  priceChangedContent: PriceChangedContent | null;
}

// =============================================================================
// Provider
// =============================================================================

export function ConfirmModalProvider({
  children,
  customer,
  isActive,
  priceChangedContent,
}: ConfirmModalProviderProps) {
  // Design Decision: Use isActive (channel-level is_allowed_visit) instead of account-level status.
  // Inactive users cannot place orders, so they don't need payment/price confirm modals.
  // @see https://github.com/marxbiotech/store-marxbiotech-com/pull/75
  const isLoggedIn = !!customer && isActive;
  const setting = customer?.salesChannelSetting ?? null;
  const isDepositEnabled = getPaymentDepositEnabledBySetting(setting);
  const isMonthlySettlement = getSettlementTypeBySetting(setting) === 'monthly';

  const [openModals, setOpenModals] = useState<ConfirmModalVisibility>({
    payment_confirm: false,
    price_changed_confirm: false,
  });
  const { setWaitingConfirmViewedIn24 } = useConfirmStorage();

  // Payment confirm hook
  const {
    isConfirmed: isPaymentConfirmed,
    showConfirmModal: showPaymentConfirmModal,
    confirm: confirmPayment,
  } = usePaymentConfirm({
    lastConfirmedAt:
      customer && isDepositEnabled
        ? customer.paymentConfirm ??
          (customer.createdAt ? toEpochMillis(customer.createdAt) : undefined)
        : undefined,
  });

  // Price changed confirm hook
  const {
    isConfirmed: isPriceChangedConfirmed,
    showConfirmModal: showPriceChangedConfirmModal,
    confirm: confirmPriceChanged,
  } = usePriceChangedConfirm({
    lastConfirmedAt: customer?.priceChangedConfirm ?? undefined,
    dateToCheck: priceChangedContent?.updatedAt
      ? toEpochMillis(priceChangedContent.updatedAt)
      : undefined,
  });

  // Skip payment confirmation for 24 hours
  const skipPaymentConfirm = useCallback(() => {
    setWaitingConfirmViewedIn24((prev) => ({
      ...prev,
      payment_confirm: true,
    }));
  }, [setWaitingConfirmViewedIn24]);

  // Open modal - checks which confirmations are needed
  const openModal = useCallback(() => {
    setOpenModals((prev: ConfirmModalVisibility) => {
      // Don't re-open if already showing
      if (prev.payment_confirm || prev.price_changed_confirm) {
        return prev;
      }
      return {
        payment_confirm: isDepositEnabled && !isPaymentConfirmed,
        price_changed_confirm: !isPriceChangedConfirmed,
      };
    });
  }, [isDepositEnabled, isPaymentConfirmed, isPriceChangedConfirmed]);

  // Close modal
  const closeModal = useCallback(
    (confirmed: boolean = false) => {
      // Use setTimeout to avoid state update during render
      setTimeout(() => {
        if (!confirmed) {
          // Mark as skipped for 24 hours
          setWaitingConfirmViewedIn24(() => ({
            ...(!isPaymentConfirmed ? { payment_confirm: true } : {}),
            ...(!isPriceChangedConfirmed
              ? { price_changed_confirm: true }
              : {}),
          }));
        }
        setOpenModals({
          payment_confirm: false,
          price_changed_confirm: false,
        });
      }, 0);
    },
    [isPaymentConfirmed, isPriceChangedConfirmed, setWaitingConfirmViewedIn24]
  );

  // Auto-open modal on mount if conditions are met
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    if (
      (isDepositEnabled && showPaymentConfirmModal) ||
      showPriceChangedConfirmModal
    ) {
      openModal();
    }
  }, [
    isLoggedIn,
    isDepositEnabled,
    showPaymentConfirmModal,
    showPriceChangedConfirmModal,
    openModal,
  ]);

  // 開啟中的項目若都已確認（例如背景 refetch 取回最新 customer）則自動關閉，
  // 避免以舊快取開啟後即使資料已更新仍需手動關閉
  useEffect(() => {
    const isAnyOpen = openModals.payment_confirm || openModals.price_changed_confirm;
    const isAllOpenConfirmed =
      (!openModals.payment_confirm || isPaymentConfirmed) &&
      (!openModals.price_changed_confirm || isPriceChangedConfirmed);
    if (isAnyOpen && isAllOpenConfirmed) {
      setOpenModals({ payment_confirm: false, price_changed_confirm: false });
    }
  }, [openModals, isPaymentConfirmed, isPriceChangedConfirmed]);

  // Compute if modal is open
  const isModalOpen =
    openModals.payment_confirm || openModals.price_changed_confirm;

  const value: ConfirmModalContextValue = {
    openModal,
    closeModal,
    isPaymentConfirmed,
    isPriceChangedConfirmed,
    isModalOpen,
    openModals,
    skipPaymentConfirm,
    confirmPayment,
    confirmPriceChanged,
    isMonthlySettlement,
    priceChangedContent,
  };

  return (
    <ConfirmModalContext.Provider value={value}>
      {children}
    </ConfirmModalContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access the confirm modal context
 * Throws an error if used outside of ConfirmModalProvider
 */
export function useConfirmModal(): ConfirmModalContextValue {
  const context = useContext(ConfirmModalContext);
  if (!context) {
    throw new Error(
      'useConfirmModal must be used within a ConfirmModalProvider'
    );
  }
  return context;
}

/**
 * Optional hook to access the confirm modal context
 * Returns null if used outside of ConfirmModalProvider (no error thrown)
 * Use this in components that may render before the provider is mounted
 */
export function useConfirmModalOptional(): ConfirmModalContextValue | null {
  return useContext(ConfirmModalContext);
}
